import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search, LogOut, ChevronDown, ChevronRight } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { http } from "@/lib/api";

export function BrandMark({ size = "md" }) {
  const top = size === "sm" ? "text-[16px]" : "text-[22px] md:text-[26px]";
  const bot = size === "sm" ? "text-[8px]" : "text-[9px] md:text-[10px]";
  return (
    <Link to="/" data-testid="brand-link" className="brand-mark">
      <span className={`brand-top ${top}`}>SIMPLE TRUTHS</span>
      <span className={`brand-bot ${bot}`}>CLUB</span>
      <span className="brand-tagline font-ui text-[7px] md:text-[8px] tracking-[0.32em] text-[#C9A055] mt-0.5 leading-tight whitespace-nowrap">
        ORDINARY THINGS · EXTRAORDINARY LESSONS
      </span>
    </Link>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nav, setNav] = useState(null);
  const [hideArt, setHideArt] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    http.get("/nav/public").then(r => setNav(r.data)).catch(() => {});
    http.get("/public/settings").then(r => setHideArt(!!r.data?.hide_art_collection)).catch(() => {});
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); setMobileOpen(false); navigate("/"); };
  const groups = nav?.groups || [];

  // URLs to hide when art collection is turned off
  const HIDDEN_ART_URLS = ["/shop", "/reflection-library", "/subscriptions", "/registry", "/truth-stand", "/what-do-you-see", "/cart", "/checkout", "/gift-cards", "/marker-shop", "/mobile-gallery"];

  // Filter items based on login requirement, device visibility, and art collection toggle
  const filterItems = (items, device = "desktop") => {
    return items.filter(item => {
      if (!item.enabled) return false;
      if (item.login_required && !user) return false;
      if (item.visible === "mobile" && device === "desktop") return false;
      if (item.visible === "desktop" && device === "mobile") return false;
      if (hideArt && HIDDEN_ART_URLS.some(u => item.url === u || item.url?.startsWith(u + "?"))) return false;
      return true;
    });
  };

  return (
    <header data-testid="site-navbar" className="sticky top-0 z-50 bg-[#050505]/95 border-b border-white/[0.06] backdrop-blur-xl">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        {/* Brand */}
        <div className="flex items-center">
          <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-[#F4F1EA] p-2 mr-2 -ml-2">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <BrandMark />
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
          {groups.map(group => (
            <NavGroup key={group.id} group={group} items={filterItems(group.items, "desktop")} />
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-flex font-ui text-[9px] tracking-[0.2em] text-[#C9A055] border border-[#C9A055]/30 px-2.5 py-1.5 hover:bg-[#C9A055]/10 rounded-sm mr-1">ADMIN</Link>
          )}
          {!hideArt && <Link to="/shop" data-testid="nav-search" className="p-2 text-[#F4F1EA]/60 hover:text-[#C9A055] transition-colors"><Search size={17} /></Link>}
          {user ? (
            <Link to="/account" data-testid="nav-account" className="p-2 text-[#F4F1EA]/60 hover:text-[#C9A055] transition-colors"><User size={17} /></Link>
          ) : (
            <Link to="/login" data-testid="nav-login" className="p-2 text-[#F4F1EA]/60 hover:text-[#C9A055] transition-colors"><User size={17} /></Link>
          )}
          {!hideArt && (
            <Link to="/cart" data-testid="nav-cart" className="relative p-2 text-[#F4F1EA]/60 hover:text-[#C9A055] transition-colors">
              <ShoppingBag size={17} />
              {count > 0 && <span data-testid="cart-count-badge" className="absolute -top-0 -right-0 bg-[#C9A055] text-black text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{count}</span>}
            </Link>
          )}
          {user && <button onClick={handleLogout} data-testid="nav-logout" className="hidden md:inline-flex p-2 text-[#F4F1EA]/30 hover:text-[#F4F1EA]" title="Sign out"><LogOut size={15} /></button>}
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <MobileMenu groups={groups} user={user} filterItems={(items) => filterItems(items, "mobile")}
          onClose={() => setMobileOpen(false)} onLogout={handleLogout} hideArt={hideArt} />
      )}
    </header>
  );
}

/* ═══ DESKTOP NAV GROUP ═══ */
function NavGroup({ group, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  const show = () => { clearTimeout(timerRef.current); setOpen(true); };
  const hide = () => { timerRef.current = setTimeout(() => setOpen(false), 150); };

  if (items.length === 0) return null;

  return (
    <div ref={ref} className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button onClick={() => setOpen(!open)} data-testid={`nav-group-${group.id}`}
        className={`font-ui text-[10px] tracking-[0.22em] px-3 py-2 flex items-center gap-1 transition-colors ${
          open ? "text-[#C9A055]" : "text-[#F4F1EA]/80 hover:text-[#F4F1EA]"
        }`}>
        {group.label}
        <ChevronDown size={10} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 min-w-[220px] bg-[#0A0A0A] border border-white/[0.08] rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.5)] py-2 mt-0.5"
          onMouseEnter={show} onMouseLeave={hide}>
          {items.map(item => (
            <NavItem key={item.id} item={item} onClose={() => setOpen(false)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══ DESKTOP NAV ITEM ═══ */
function NavItem({ item, onClose }) {
  const [subOpen, setSubOpen] = useState(false);
  const hasChildren = (item.children || []).filter(c => c.enabled).length > 0;

  if (hasChildren) {
    return (
      <div className="relative" onMouseEnter={() => setSubOpen(true)} onMouseLeave={() => setSubOpen(false)}>
        <Link to={item.url} onClick={onClose} target={item.new_tab ? "_blank" : undefined}
          className="flex items-center justify-between px-4 py-2.5 font-ui text-[11px] tracking-[0.1em] text-[#F4F1EA]/80 hover:text-[#C9A055] hover:bg-white/[0.03] transition-all">
          {item.label}
          <ChevronRight size={10} className="text-zinc-600" />
        </Link>
        {subOpen && (
          <div className="absolute top-0 left-full min-w-[200px] bg-[#0A0A0A] border border-white/[0.08] rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.5)] py-2 ml-0.5">
            {item.children.filter(c => c.enabled).map(child => (
              <Link key={child.id} to={child.url} onClick={onClose} target={child.new_tab ? "_blank" : undefined}
                className="block px-4 py-2.5 font-ui text-[11px] tracking-[0.1em] text-[#F4F1EA]/70 hover:text-[#C9A055] hover:bg-white/[0.03] transition-all">
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link to={item.url} onClick={onClose} target={item.new_tab ? "_blank" : undefined}
      className="block px-4 py-2.5 font-ui text-[11px] tracking-[0.1em] text-[#F4F1EA]/80 hover:text-[#C9A055] hover:bg-white/[0.03] transition-all">
      {item.label}
    </Link>
  );
}

/* ═══ MOBILE MENU ═══ */
function MobileMenu({ groups, user, filterItems, onClose, onLogout, hideArt }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [openSub, setOpenSub] = useState(null);

  return (
    <div data-testid="mobile-menu" className="lg:hidden fixed inset-x-0 top-[72px] h-[calc(100vh-72px)] z-40 bg-[#050505] overflow-y-auto">
      <div className="px-6 py-6 space-y-1">
        {groups.map(group => {
          const items = filterItems(group.items);
          if (items.length === 0) return null;
          const isOpen = openGroup === group.id;
          return (
            <div key={group.id} className="border-b border-white/[0.04] pb-1">
              <button onClick={() => setOpenGroup(isOpen ? null : group.id)}
                className="w-full flex items-center justify-between py-3 text-left" data-testid={`mobile-group-${group.id}`}>
                <span className="font-ui text-[9px] tracking-[0.3em] text-[#C9A055]">{group.label}</span>
                <ChevronDown size={12} className={`text-zinc-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="pl-3 pb-3 space-y-0.5">
                  {items.map(item => {
                    const children = (item.children || []).filter(c => c.enabled);
                    const subIsOpen = openSub === item.id;
                    return (
                      <div key={item.id}>
                        <div className="flex items-center">
                          <Link to={item.url} onClick={onClose} target={item.new_tab ? "_blank" : undefined}
                            className="flex-1 py-2.5 font-ui text-[12px] tracking-[0.15em] text-[#F4F1EA]/80 hover:text-[#C9A055]">{item.label}</Link>
                          {children.length > 0 && (
                            <button onClick={() => setOpenSub(subIsOpen ? null : item.id)} className="p-2 text-zinc-600">
                              <ChevronDown size={12} className={`transition-transform ${subIsOpen ? "rotate-180" : ""}`} />
                            </button>
                          )}
                        </div>
                        {subIsOpen && children.length > 0 && (
                          <div className="pl-4 pb-2 space-y-0.5">
                            {children.map(child => (
                              <Link key={child.id} to={child.url} onClick={onClose} target={child.new_tab ? "_blank" : undefined}
                                className="block py-2 font-ui text-[11px] tracking-[0.12em] text-[#F4F1EA]/60 hover:text-[#C9A055]">{child.label}</Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Account section */}
        <div className="pt-4 space-y-3">
          {user ? (
            <>
              <Link to="/account" onClick={onClose} className="block font-ui text-[12px] tracking-[0.15em] text-[#F4F1EA]/80">My Account</Link>
              {user.role === "admin" && <Link to="/admin" onClick={onClose} className="block font-ui text-[12px] tracking-[0.15em] text-[#C9A055]">Admin Dashboard</Link>}
              {user.role === "partner" && <Link to="/partner" onClick={onClose} className="block font-ui text-[12px] tracking-[0.15em] text-[#C9A055]">Partner Dashboard</Link>}
              <button onClick={onLogout} className="block font-ui text-[12px] tracking-[0.15em] text-[#F4F1EA]/30">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={onClose} className="block font-ui text-[12px] tracking-[0.15em] text-[#F4F1EA]/80">Sign In</Link>
              <Link to="/register" onClick={onClose} className="block font-ui text-[12px] tracking-[0.15em] text-[#C9A055]">Create Account</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
